import React, { useCallback, useEffect, useState } from "react";
import { Box, Text, Grid } from "grommet";
import * as tf from "@tensorflow/tfjs";
import { SmallScreenMessage } from "../components/small-screen-message";
import { Footer } from "../components/footer";
import { HeaderArea } from "../components/header-area";
import { LeftSidebar } from "../components/left-sidebar";
import scaler from "../task-specific/initializations/scaler.json";
import { RightSidebar } from "../components/right-sidebar";
import { useLoadModel } from "../hooks";
import { Main } from "../components/main";

const ECG_LENGTH = 150;

const YShift = 3;
const XShift = ECG_LENGTH;
const plotShifter: { x: number; y: number }[] = [
  { x: 0, y: YShift },
  { x: 0, y: 0 },
  { x: 0, y: -YShift },
  { x: XShift, y: YShift },
  { x: XShift, y: 0 },
  { x: XShift, y: -YShift },
  { x: XShift * 2, y: YShift },
  { x: XShift * 2, y: 0 },
  { x: XShift * 2, y: -YShift },
  { x: XShift * 3, y: YShift },
  { x: XShift * 3, y: 0 },
  { x: XShift * 3, y: -YShift },
];

export function AuthenticatedApp({ signOut }: any): React.ReactElement {
  const { decoder, breakpoint, minHeight, isModelLoaded, backendType, progress } = useLoadModel();

  const [sliders, setSliders] = useState<number[]>(Array.from(scaler.mean));
  const [lastSliderUpdate, setLastSliderUpdate] = useState<any>(null);
  const [ecg, setEcg] = useState<any>();
  const [originalEcg, setOriginalEcg] = useState<any>();

  const handleSlidersChange = useCallback(
    (updatedSliderValues) => {
      setSliders(updatedSliderValues);
      setLastSliderUpdate(Date.now());
    },
    [setSliders]
  );

  const handleSliderChange = useCallback(
    (newValue, sliderIndex) => {
      const updatedSliderValues = sliders;
      updatedSliderValues[sliderIndex] = newValue;
      handleSlidersChange(updatedSliderValues);
    },
    [sliders, setSliders]
  );

  const handleResetSliders = () => {
    setSliders(Array.from(scaler.mean));
    setLastSliderUpdate(Date.now());
  };

  const handleUploadSliders = (upload_values: Array<number>) => {
    setSliders(Array.from(upload_values));
    setLastSliderUpdate(Date.now());
  };

  useEffect(() => {
    if (decoder) {
      const output = decoder && decoder.predict(tf.reshape(tf.tensor1d(sliders), [1, 32]));
      if (output) {
        const meanOut: any = tf.reshape(output[0], [12 * ECG_LENGTH]);
        if (originalEcg == null) {
          const ecg = Array.from({ length: 12 }, (x, i) => tf.slice(meanOut, i * ECG_LENGTH, ECG_LENGTH)).map((leadSlice, leadIndex) => ({
            id: `Lead ${leadIndex} orginal`,
            data: (Array.from(leadSlice.dataSync()) as any).map((y: number, x: number) => ({
              x: x + plotShifter[leadIndex]["x"],
              y: y + plotShifter[leadIndex]["y"],
            })),
          }));
          setOriginalEcg(ecg);
        } else {
          const ecg = Array.from({ length: 12 }, (x, i) => tf.slice(meanOut, i * ECG_LENGTH, ECG_LENGTH)).map((leadSlice, leadIndex) => ({
            id: `Lead ${leadIndex}`,
            data: (Array.from(leadSlice.dataSync()) as any).map((y: number, x: number) => ({
              x: x + plotShifter[leadIndex]["x"],
              y: y + plotShifter[leadIndex]["y"],
            })),
          }));
          setEcg(ecg);
        }
      }
    }
  }, [decoder, lastSliderUpdate]);

  return (
    <Box height="100vh" width={{ max: "1800px" }} margin="auto">
      {breakpoint === "tooSmall" ? (
        <SmallScreenMessage />
      ) : (
        <Grid
          rows={["80px", "flex", "40px"]}
          columns={["260px", "auto", "260px"]}
          gap={{
            row: "small",
            column: "40px",
          }}
          pad={{ horizontal: "40px", bottom: "40px" }}
          areas={[
            { name: "header", start: [0, 0], end: [2, 0] },
            { name: "right-sidebar", start: [2, 1], end: [2, 2] },
            { name: "left-sidebar", start: [0, 1], end: [0, 1] },
            { name: "main", start: [1, 1], end: [1, 2] },
            { name: "sign-out", start: [0, 2], end: [0, 2] },
            { name: "ecg-info", start: [2, 2], end: [2, 2] },
          ]}
          fill={true}
        >
          <HeaderArea isModelLoaded={isModelLoaded} backendType={backendType} breakpoint={breakpoint} />
          <LeftSidebar handleUploadSliders={handleUploadSliders} />
          <Main
            isModelLoaded={isModelLoaded}
            progress={progress}
            ecg={ecg}
            originalEcg={originalEcg}
            sliders={sliders}
            onResetSliders={handleResetSliders}
            onSliderChange={handleSliderChange}
          />
          <RightSidebar sliders={sliders} onSlidersChange={handleSlidersChange} />
          <Footer signOut={signOut} />
          <Box gridArea="ecg-info" align="center" justify="center">
            <Text>1 box = 200 ms by 0.5 mV</Text>
          </Box>
        </Grid>
      )}
    </Box>
  );
}
